export type SyncErrorKind = 'validation' | 'upload' | 'unknown';

export type StoredSyncError =
  | {
    version: 1;
    kind: 'validation';
    name?: string;
    attributes: Record<string, string[]>;
  }
  | {
    version: 1;
    kind: 'upload' | 'unknown';
    name?: string;
    errors: string[];
  };

export type ParsedSyncError = StoredSyncError;
export type SyncErrorInput = ParsedSyncError | string | null | undefined;

const UNKNOWN_ERROR = 'Unknown Error';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const asStoredSyncError = (value: unknown): StoredSyncError | null => {
  if (!isRecord(value) || value.version !== 1) {
    return null;
  }

  if (value.kind === 'validation' && isRecord(value.attributes)) {
    return {
      version: 1,
      kind: 'validation',
      name: typeof value.name === 'string' ? value.name : undefined,
      attributes: Object.entries(value.attributes).reduce((acc, [path, messages]) => {
        const stringMessages = asStringArray(messages);
        if (stringMessages.length > 0) {
          acc[path] = stringMessages;
        }
        return acc;
      }, {} as Record<string, string[]>)
    };
  }

  if ((value.kind === 'upload' || value.kind === 'unknown') && Array.isArray(value.errors)) {
    return {
      version: 1,
      kind: value.kind,
      name: typeof value.name === 'string' ? value.name : undefined,
      errors: asStringArray(value.errors)
    };
  }

  return null;
};

const asStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
  if (typeof value === 'string' && value.length > 0) {
    return [value];
  }
  return [];
};

const mergeAttributeMessages = (
  target: Record<string, string[]>,
  source: unknown
) => {
  if (!isRecord(source)) {
    return;
  }

  for (const [path, value] of Object.entries(source)) {
    const messages = asStringArray(value);
    if (messages.length === 0) {
      continue;
    }
    target[path] = [...(target[path] ?? []), ...messages];
  }
};

const extractValidationAttributes = (error: unknown): Record<string, string[]> => {
  const attributes: Record<string, string[]> = {};
  if (!isRecord(error) || !Array.isArray(error.errors)) {
    return attributes;
  }

  for (const item of error.errors) {
    if (!isRecord(item)) {
      continue;
    }
    mergeAttributeMessages(attributes, item.original ?? item.message);
  }

  return attributes;
};

const getErrorName = (error: unknown): string | undefined => {
  return isRecord(error) && typeof error.name === 'string' ? error.name : undefined;
};

const getGeneralErrorMessages = (error: unknown, fallback = UNKNOWN_ERROR): string[] => {
  if (typeof error === 'string' && error.length > 0) {
    return [error];
  }
  if (isRecord(error)) {
    if (typeof error.message === 'string' && error.message.length > 0) {
      return [error.message];
    }
    if (typeof error.name === 'string' && error.name.length > 0) {
      return [error.name];
    }
  }
  return [fallback];
};

export const normaliseSyncError = (error: unknown, fallback = UNKNOWN_ERROR): StoredSyncError => {
  const stored = asStoredSyncError(error);
  if (stored) {
    return stored;
  }

  const name = getErrorName(error);
  const attributes = extractValidationAttributes(error);

  if (Object.keys(attributes).length > 0) {
    return {
      version: 1,
      kind: 'validation',
      name,
      attributes
    };
  }

  if (name === 'SequelizeValidationError') {
    return {
      version: 1,
      kind: 'validation',
      name,
      attributes: {}
    };
  }

  return {
    version: 1,
    kind: name ? 'upload' : 'unknown',
    name,
    errors: getGeneralErrorMessages(error, fallback)
  };
};

export const serialiseSyncError = (error: unknown, fallback = UNKNOWN_ERROR): string => {
  return JSON.stringify(normaliseSyncError(error, fallback));
};

export const parseSyncError = (syncError?: SyncErrorInput): ParsedSyncError | null => {
  if (!syncError) {
    return null;
  }

  const stored = asStoredSyncError(syncError);
  if (stored) {
    return stored;
  }

  if (typeof syncError !== 'string') {
    return normaliseSyncError(syncError);
  }

  try {
    const parsed = JSON.parse(syncError);
    const parsedStored = asStoredSyncError(parsed);
    if (parsedStored) {
      return parsedStored;
    }

    if (isRecord(parsed) && Array.isArray(parsed.errors)) {
      return {
        version: 1,
        kind: parsed.kind === 'upload' ? 'upload' : 'unknown',
        name: typeof parsed.name === 'string' ? parsed.name : undefined,
        errors: asStringArray(parsed.errors)
      };
    }
  } catch {
    return normaliseSyncError(syncError);
  }

  return normaliseSyncError(syncError);
};

export const getSyncErrorAttributeMessages = (syncError?: SyncErrorInput): { path: string; messages: string[] }[] => {
  const parsed = parseSyncError(syncError);
  if (!parsed || parsed.kind !== 'validation') {
    return [];
  }

  return Object.entries(parsed.attributes).map(([path, messages]) => ({
    path,
    messages
  }));
};

export const getSyncErrorMessages = (syncError?: SyncErrorInput): string[] => {
  const parsed = parseSyncError(syncError);
  if (!parsed) {
    return [];
  }
  if (parsed.kind === 'validation') {
    return getSyncErrorAttributeMessages(syncError).flatMap(item => item.messages);
  }
  return parsed.errors;
};

export const getSyncErrorSummary = (syncError?: SyncErrorInput): string => {
  const parsed = parseSyncError(syncError);
  if (!parsed) {
    return '';
  }

  if (parsed.kind === 'validation') {
    return 'This record has validation issues - edit to fix them';
  }

  return parsed.errors[0] ?? UNKNOWN_ERROR;
};

export const clearSyncErrorAttribute = (syncError: SyncErrorInput, path: string): ParsedSyncError | null => {
  const parsed = parseSyncError(syncError);
  if (!parsed || parsed.kind !== 'validation') {
    return parsed;
  }
  const { [path]: _removed, ...attributes } = parsed.attributes;
  if (Object.keys(attributes).length === 0) {
    return null;
  }
  return {
    version: 1,
    kind: 'validation',
    name: parsed.name,
    attributes
  };
};

export const serialiseRecordSyncErrorForStorage = (syncError: ParsedSyncError | null | undefined): string | null => {
  if (!syncError) {
    return null;
  }
  return JSON.stringify(syncError);
};

/** True when a POST /sync/.../records response entry is a persisted record, not an error payload. */
export const isRecordUploadSuccessResponse = (entry: unknown): boolean => {
  return typeof entry === 'object' && entry !== null &&
    typeof (entry as { id?: unknown }).id === 'number';
};

export type RecordIssueStatus = 'none' | 'validation' | 'sync' | 'inherited';

export type RecordIssueInput = {
  syncError?: ParsedSyncError | null;
  treeSyncError?: number | null;
};

export const hasDirectRecordError = (record: RecordIssueInput): boolean => {
  return !!record.syncError;
};

export const hasInheritedRecordError = (record: RecordIssueInput): boolean => {
  return !!record.treeSyncError;
};

export const hasRecordError = (record: RecordIssueInput): boolean => {
  return hasDirectRecordError(record) || hasInheritedRecordError(record);
};

export const isValidationIssue = (record: RecordIssueInput): boolean => {
  return record.syncError?.kind === 'validation' || !!record.treeSyncError;
};

export const getRecordIssueStatus = (record: RecordIssueInput): RecordIssueStatus => {
  if (!hasRecordError(record)) {
    return 'none';
  }
  if (hasInheritedRecordError(record) && !hasDirectRecordError(record)) {
    return 'inherited';
  }
  if (record.syncError?.kind === 'validation') {
    return 'validation';
  }
  if (hasDirectRecordError(record)) {
    return 'sync';
  }
  return 'inherited';
};

export const getRecordIssueLabel = (record: RecordIssueInput): string | null => {
  if (!hasRecordError(record)) {
    return null;
  }
  return isValidationIssue(record) ? 'Validation Issue' : 'Sync Error';
};

export const getInheritedRecordErrorSummary = (): string => {
  return 'A child record has a synchronisation error';
};

export const getSyncErrorMessagesForPath = (syncError: SyncErrorInput, path: string | undefined): string[] | null => {
  const parsed = parseSyncError(syncError);
  if (!parsed || parsed.kind !== 'validation' || !path) {
    return null;
  }
  const messages = parsed.attributes[path];
  if (!messages || messages.length === 0) {
    return null;
  }
  return messages;
};